class ColumnNavigation extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.hover = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.drawer = this.querySelector('.column-navigation__drawer');
    this.launch = this.querySelector('[data-drawer-toggle]');
    this.items = [...this.querySelectorAll('.column-navigation__item')];
    this.launch.addEventListener('click', () => this.openDrawer(), options);
    this.querySelectorAll('[data-drawer-close]').forEach(button => button.addEventListener('click', () => this.close(true), options));
    this.items.forEach(item => {
      const toggle = item.querySelector('[data-panel-toggle]');
      if (!toggle) return;
      toggle.addEventListener('click', () => this.setPanel(item, !item.hasAttribute('data-open'), true), options);
      item.querySelector('[data-panel-back]').addEventListener('click', () => this.setPanel(item, false, true), options);
      item.addEventListener('pointerenter', () => {
        if (!this.desktop.matches || !this.hover.matches) return;
        clearTimeout(this.closeTimer);
        this.openTimer = setTimeout(() => this.setPanel(item, true), 70);
      }, options);
      item.addEventListener('pointerleave', () => {
        clearTimeout(this.openTimer);
        if (!this.desktop.matches || item.contains(document.activeElement)) return;
        this.closeTimer = setTimeout(() => this.setPanel(item, false), 180);
      }, options);
      item.addEventListener('focusin', () => {
        if (this.desktop.matches && document.activeElement.matches(':focus-visible')) this.setPanel(item, true);
      }, options);
    });
    this.addEventListener('focusout', () => {
      setTimeout(() => {
        if (this.desktop.matches && !this.contains(document.activeElement)) this.close();
      });
    }, options);
    this.addEventListener('keydown', event => {
      if (event.key === 'Tab' && !this.desktop.matches && this.hasAttribute('data-drawer-open')) {
        const active = this.items.find(candidate => candidate.hasAttribute('data-open'));
        const container = active ? active.querySelector('.column-menu__panel') : this.drawer;
        const focusable = [...container.querySelectorAll('a[href], button, select, input, [tabindex="0"]')]
          .filter(element => element.getClientRects().length && !element.disabled && !element.closest('[inert]'));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
      if (event.key !== 'Escape') return;
      const item = this.items.find(candidate => candidate.hasAttribute('data-open'));
      event.preventDefault();
      event.stopPropagation();
      if (item) this.setPanel(item, false, true);
      else this.close(true);
    }, options);
    document.addEventListener('pointerdown', event => {
      if (!this.contains(event.target)) this.close();
    }, options);
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || event.defaultPrevented || this.contains(event.target) || !this.desktop.matches) return;
      const item = this.items.find(candidate => candidate.hasAttribute('data-open'));
      if (item) {
        event.preventDefault();
        this.setPanel(item, false, true);
      }
    }, options);
    this.onBreakpoint = () => {
      const focusWasInside = this.contains(document.activeElement);
      this.close();
      if (focusWasInside) {
        const target = this.desktop.matches ? this.querySelector('nav a, nav button') : this.launch;
        target?.focus();
      }
    };
    this.desktop.addEventListener('change', this.onBreakpoint);
    this.syncAccessibility();
  }

  openDrawer() {
    if (this.desktop.matches) return;
    this.setAttribute('data-drawer-open', '');
    this.launch.setAttribute('aria-expanded', 'true');
    document.body.classList.add('column-navigation-open');
    this.drawer.setAttribute('role', 'dialog');
    this.drawer.setAttribute('aria-modal', 'true');
    this.drawer.setAttribute('aria-label', this.launch.getAttribute('aria-label'));
    this.syncAccessibility();
    this.drawer.querySelector('[data-drawer-close]').focus();
  }

  setPanel(item, open, moveFocus = false) {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    if (open) this.items.forEach(other => {
      if (other !== item) {
        other.removeAttribute('data-open');
        other.querySelector('[data-panel-toggle]')?.setAttribute('aria-expanded', 'false');
      }
    });
    item.toggleAttribute('data-open', open);
    const toggle = item.querySelector('[data-panel-toggle]');
    toggle.setAttribute('aria-expanded', String(open));
    if (open) {
      document.documentElement.style.setProperty('--header-bottom-position-desktop', `${Math.floor(this.closest('.header-wrapper').getBoundingClientRect().bottom)}px`);
    }
    this.syncAccessibility();
    if (!this.desktop.matches && this.hasAttribute('data-drawer-open')) {
      const target = open ? item.querySelector('.column-menu__panel') : this.drawer;
      (open ? target.querySelector('[data-panel-back]') : toggle).focus();
    } else if (!open && moveFocus) {
      toggle.focus();
      // Focusing a desktop toggle must not reopen the panel just dismissed.
      item.removeAttribute('data-open');
      toggle.setAttribute('aria-expanded', 'false');
      this.syncAccessibility();
    }
  }

  syncAccessibility() {
    const mobile = !this.desktop.matches;
    const headerScheme = this.drawer.dataset.headerScheme;
    const menuScheme = this.drawer.dataset.menuScheme;
    if (headerScheme && menuScheme) {
      this.drawer.classList.remove(headerScheme, menuScheme);
      this.drawer.classList.add(mobile ? menuScheme : headerScheme);
    }
    const active = this.items.find(item => item.hasAttribute('data-open'));
    this.items.forEach(item => {
      item.inert = mobile && Boolean(active) && item !== active;
      const toggle = item.querySelector('[data-panel-toggle]');
      if (toggle) {
        const content = item.querySelector(mobile ? '.column-menu__panel' : '.column-menu__body');
        toggle.setAttribute('aria-controls', content.id);
      }
    });
    const toolbar = this.querySelector('.column-navigation__toolbar');
    const utilities = this.querySelector('.column-navigation__utilities');
    toolbar.inert = mobile && Boolean(active);
    utilities.inert = mobile && Boolean(active);
    const sticky = this.closest('sticky-header');
    if (sticky) sticky.preventHide = Boolean(active) || this.hasAttribute('data-drawer-open');
  }

  close(restoreFocus = false) {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    const wasOpen = this.hasAttribute('data-drawer-open');
    this.removeAttribute('data-drawer-open');
    this.launch.setAttribute('aria-expanded', 'false');
    this.drawer.removeAttribute('role');
    this.drawer.removeAttribute('aria-modal');
    this.drawer.removeAttribute('aria-label');
    this.items.forEach(item => {
      item.removeAttribute('data-open');
      item.querySelector('[data-panel-toggle]')?.setAttribute('aria-expanded', 'false');
    });
    if (wasOpen) {
      document.body.classList.remove('column-navigation-open');
      if (restoreFocus && !this.desktop.matches) this.launch.focus();
    }
    this.syncAccessibility();
  }

  disconnectedCallback() {
    this.close();
    this.abort.abort();
    this.desktop.removeEventListener('change', this.onBreakpoint);
  }
}

if (!customElements.get('column-navigation')) customElements.define('column-navigation', ColumnNavigation);
